import React, { useRef, useState, useEffect } from 'react'
import { FileUp } from 'lucide-react'
import { Button, Track } from '../../school_panel/components/ui'
import { useAuth } from '../../utils/Auth/AuthProvider'
import { createExtractionJob, getExtractionJob, retryExtractionJob } from '../../utils/remote'
import type { ExtractionJob } from '../../utils/remote'

const STORAGE_KEY = 'yada.extractJob'
const POLL_MS = 2000
const MAX_POLL_ERRORS = 3

// One stored job per scope (e.g. per exam), so two exams do not share a job.
const storageKey = (scope?: string) => (scope ? `${STORAGE_KEY}.${scope}` : STORAGE_KEY)

const readStoredId = (key: string): number | null => {
    try {
        const raw = window.sessionStorage.getItem(key)
        const id = raw ? Number(raw) : NaN
        return Number.isFinite(id) && id > 0 ? id : null
    } catch { return null }
}
const writeStoredId = (key: string, id: number | null) => {
    try {
        if (id === null) window.sessionStorage.removeItem(key)
        else window.sessionStorage.setItem(key, String(id))
    } catch { /* storage unavailable */ }
}

// [7, 8, 9, 12] -> "7–9, 12"
const formatPages = (pages: number[]) => {
    const sorted = Array.from(new Set(pages)).sort((a, b) => a - b)
    const parts: string[] = []
    let i = 0
    while (i < sorted.length) {
        let j = i
        while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1) j++
        parts.push(j > i ? `${sorted[i]}–${sorted[j]}` : String(sorted[i]))
        i = j + 1
    }
    return parts.join(', ')
}

// Failed pages that share a message become one line: "Pages 7–8: <message>".
const groupFailures = (failed: { pages: number[]; message: string }[]) => {
    const groups = new Map<string, number[]>()
    for (const f of failed) {
        const msg = (f.message || '').trim()
        groups.set(msg, [...(groups.get(msg) || []), ...f.pages])
    }
    return Array.from(groups, ([message, pages]) => {
        const label = `${new Set(pages).size === 1 ? 'Page' : 'Pages'} ${formatPages(pages)}`
        return message ? `${label}: ${message}` : `${label} could not be read.`
    })
}

const NO_QUESTIONS = 'No questions were found in this file.'
const LOST_CONTACT = 'We lost contact with the server. Reload the page to check this file again.'

const errorDetail = (err: any, fallback: string): string => {
    const detail = err?.response?.data?.detail
    return typeof detail === 'string' && detail ? detail : fallback
}

interface ExtractProps {
    onExtract: (questions: any[]) => void;
    onRegisterExtractFn?: (fn: () => void) => void;
    isExtracting: boolean;
    setIsExtracting: React.Dispatch<React.SetStateAction<boolean>>;
    proposeAnswer?: boolean;
    proposeExplanation?: boolean;
    /** Scopes the remembered job (e.g. the exam id): `yada.extractJob.<scope>`. */
    jobScope?: string;
}

const ExtractQuestionsFromFile = ({ onExtract, onRegisterExtractFn, isExtracting, setIsExtracting, proposeAnswer = false, proposeExplanation = false, jobScope }: ExtractProps) => {
    const { token } = useAuth()
    const fileRef = useRef<File | undefined>(undefined)
    const [extractError, setExtractError] = useState<string | null>(null)
    const [job, setJob] = useState<ExtractionJob | null>(null)
    const [doneJob, setDoneJob] = useState<ExtractionJob | null>(null)
    const [failedJob, setFailedJob] = useState(false)
    const [showPages, setShowPages] = useState(false)
    const [pageFrom, setPageFrom] = useState('')
    const [pageTo, setPageTo] = useState('')
    const [lostContact, setLostContact] = useState(false)
    const [notice, setNotice] = useState<string | null>(null)

    const keyRef = useRef(storageKey(jobScope))
    keyRef.current = storageKey(jobScope)
    const storeId = (id: number | null) => writeStoredId(keyRef.current, id)

    // Latest values for callbacks that outlive a render (registered trigger, poll loop).
    const latest = useRef({ token, onExtract, setIsExtracting, proposeAnswer, proposeExplanation, pageFrom, pageTo, showPages })
    latest.current = { token, onExtract, setIsExtracting, proposeAnswer, proposeExplanation, pageFrom, pageTo, showPages }

    const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const gen = useRef(0)
    const unmounted = useRef(false)
    const pollErrors = useRef(0)

    const stopTimer = () => {
        if (timer.current) { clearTimeout(timer.current); timer.current = null }
    }

    const poll = async (id: number, g: number) => {
        timer.current = null
        let next: ExtractionJob
        try {
            next = await getExtractionJob(latest.current.token, id)
        } catch (err: any) {
            if (g !== gen.current) return
            if (err?.response?.status === 404) {
                storeId(null)
                setJob(null)
                latest.current.setIsExtracting(false)
                return
            }
            pollErrors.current += 1
            if (pollErrors.current >= MAX_POLL_ERRORS) {
                setExtractError(LOST_CONTACT)
                setLostContact(true)
                latest.current.setIsExtracting(false)
                return
            }
            timer.current = setTimeout(() => poll(id, g), POLL_MS)
            return
        }
        if (g !== gen.current) return
        pollErrors.current = 0
        setJob(next)
        if (next.status === 'queued' || next.status === 'running') {
            timer.current = setTimeout(() => poll(id, g), POLL_MS)
            return
        }
        if (next.status === 'failed') {
            storeId(null)
            setJob(null)
            setFailedJob(true)
            setExtractError(next.error || 'We could not read questions from this file.')
            latest.current.setIsExtracting(false)
            return
        }
        // done
        const questions = next.questions || []
        if (next.failed_pages && next.failed_pages.length > 0) {
            setDoneJob(next)
            latest.current.setIsExtracting(false)
            return
        }
        storeId(null)
        setJob(null)
        if (questions.length === 0) {
            // Nothing found and nothing failed: say so instead of handing over an empty list.
            setNotice(NO_QUESTIONS)
            latest.current.setIsExtracting(false)
            return
        }
        latest.current.onExtract(questions)
        latest.current.setIsExtracting(false)
    }

    // Stop following the current job and show the upload box again.
    const startOver = () => {
        gen.current++
        stopTimer()
        storeId(null)
        pollErrors.current = 0
        setJob(null)
        setDoneJob(null)
        setFailedJob(false)
        setLostContact(false)
        setExtractError(null)
        setNotice(null)
        latest.current.setIsExtracting(false)
    }

    const pageRangeError = (() => {
        const from = pageFrom.trim(), to = pageTo.trim()
        if (!showPages) return null
        if (from && to && Number(to) < Number(from)) return 'To must be the same as or after From.'
        return null
    })()

    const triggerExtract = async () => {
        const cur = latest.current
        const file = fileRef.current
        setExtractError(null)
        setFailedJob(false)
        setLostContact(false)
        setNotice(null)
        if (!file) {
            setExtractError('Select a file first.')
            return
        }
        let from: number | undefined
        let to: number | undefined
        // Page ranges apply to PDFs only.
        if (cur.showPages && file.name.toLowerCase().endsWith('.pdf')) {
            from = cur.pageFrom.trim() ? Number(cur.pageFrom) : undefined
            to = cur.pageTo.trim() ? Number(cur.pageTo) : undefined
            if (from !== undefined && to !== undefined && to < from) return
        }
        setDoneJob(null)
        cur.setIsExtracting(true)
        try {
            const created = await createExtractionJob({
                toBeValidated: cur.token, file, pageFrom: from, pageTo: to,
                proposeAnswer: cur.proposeAnswer, proposeExplanation: cur.proposeExplanation,
            })
            // Remember the job even if the form went away meanwhile: it resumes on return.
            storeId(created.id)
            if (unmounted.current) return
            setJob(created)
            pollErrors.current = 0
            stopTimer()
            const g = ++gen.current
            timer.current = setTimeout(() => poll(created.id, g), POLL_MS)
        } catch (err: any) {
            if (unmounted.current) return
            setExtractError(errorDetail(err, 'We could not upload this file. Try again.'))
            cur.setIsExtracting(false)
        }
    }
    const triggerRef = useRef(triggerExtract)
    triggerRef.current = triggerExtract

    useEffect(() => {
        onRegisterExtractFn?.(() => triggerRef.current())
    }, [onRegisterExtractFn])

    // Resume a job that was running before a reload.
    useEffect(() => {
        unmounted.current = false
        const g = ++gen.current
        const id = readStoredId(keyRef.current)
        if (id !== null) {
            latest.current.setIsExtracting(true)
            poll(id, g)
        }
        return () => {
            gen.current++; unmounted.current = true; stopTimer()
            // Not following the job any more (the resume above sets it again on return).
            latest.current.setIsExtracting(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const retryPages = async () => {
        if (!doneJob) return
        setExtractError(null)
        latest.current.setIsExtracting(true)
        try {
            const next = await retryExtractionJob(latest.current.token, doneJob.id)
            storeId(next.id)
            if (unmounted.current) return
            setDoneJob(null)
            setJob(next)
            pollErrors.current = 0
            stopTimer()
            const g = ++gen.current
            timer.current = setTimeout(() => poll(next.id, g), POLL_MS)
        } catch (err: any) {
            if (unmounted.current) return
            setExtractError(errorDetail(err, 'We could not retry those pages.'))
            latest.current.setIsExtracting(false)
        }
    }

    const continueWithFound = () => {
        if (!doneJob) return
        const questions = doneJob.questions || []
        storeId(null)
        setDoneJob(null)
        setJob(null)
        latest.current.onExtract(questions)
        latest.current.setIsExtracting(false)
    }

    const changeFileData = (e: React.ChangeEvent<HTMLInputElement>) => {
        fileRef.current = e.target.files?.[0]
        setExtractError(null)
    }

    const prog = job?.progress
    const reading = !prog || prog.pages_done <= 0 || prog.pages_total <= 0
    const pct = prog && prog.pages_total > 0 ? (prog.pages_done / prog.pages_total) * 100 : 0
    const found = prog?.questions_found ?? 0

    return (
        <div>
            <div className='rounded-[18px] border-2 border-dashed border-line-strong bg-surface-2 px-5 py-8 text-center'>
                <span aria-hidden className='mx-auto mb-3 grid h-[52px] w-[52px] place-items-center rounded-[14px] bg-mist text-slate'><FileUp size={22} strokeWidth={1.8} /></span>
                <h3 className='font-display text-[17px] font-bold text-ink'><label htmlFor='extract-question-doc'>Choose a document (.pdf, .docx)</label></h3>
                <p className='mx-auto mt-1.5 max-w-[46ch] text-[13px] text-muted'>Extracted questions are added to the list on the left for you to check before saving.</p>
                <input id='extract-question-doc' title='question doc' type='file' accept='.pdf,.doc,.docx,.png,.jpg,.jpeg' onChange={changeFileData} disabled={isExtracting}
                    className='mx-auto mt-4 block w-full max-w-[420px] cursor-pointer text-sm text-muted file:mr-3 file:min-h-[40px] file:cursor-pointer file:rounded-[12px] file:border-0 file:bg-navy file:px-4 file:text-sm file:font-semibold file:text-white hover:file:bg-navy-2 disabled:cursor-not-allowed disabled:opacity-60' />

                <div className='mt-3'>
                    <button type='button' aria-expanded={showPages} onClick={() => setShowPages((v) => !v)} disabled={isExtracting}
                        className='text-[13px] font-semibold text-slate underline-offset-2 hover:underline disabled:opacity-60'>Only some pages?</button>
                    {showPages && (
                        <div className='mx-auto mt-2 flex max-w-[420px] items-center justify-center gap-3 text-sm text-muted'>
                            <label className='flex items-center gap-2'>From
                                <input type='number' min={1} value={pageFrom} onChange={(e) => setPageFrom(e.target.value)} disabled={isExtracting}
                                    className='w-20 rounded-[10px] border border-line bg-white px-2 py-1.5 text-ink' />
                            </label>
                            <label className='flex items-center gap-2'>To
                                <input type='number' min={1} value={pageTo} onChange={(e) => setPageTo(e.target.value)} disabled={isExtracting}
                                    className='w-20 rounded-[10px] border border-line bg-white px-2 py-1.5 text-ink' />
                            </label>
                        </div>
                    )}
                    {showPages && <p className='mt-1.5 text-xs text-muted'>Pages apply to PDF files only. Leave both empty for the whole file.</p>}
                    {pageRangeError && <p className='mt-1.5 text-xs text-danger-ink'>{pageRangeError}</p>}
                </div>
            </div>

            {extractError && (
                <p role="alert" className="mt-3 rounded-[12px] border border-danger/30 bg-danger-bg px-3.5 py-2.5 text-sm text-danger-ink">{extractError}</p>
            )}
            {extractError && failedJob && fileRef.current && !isExtracting && (
                <div className='mt-2'><Button size='sm' variant='secondary' onClick={() => triggerRef.current()}>Try again</Button></div>
            )}
            {lostContact && !isExtracting && (
                <div className='mt-2'><Button size='sm' variant='secondary' onClick={startOver}>Start over</Button></div>
            )}
            {notice && (
                <p role="status" className="mt-3 rounded-[12px] border border-line bg-mist px-3.5 py-2.5 text-sm text-ink">{notice}</p>
            )}

            {doneJob && doneJob.error && (
                <p role="alert" className="mt-3 rounded-[12px] border border-danger/30 bg-danger-bg px-3.5 py-2.5 text-sm text-danger-ink">{doneJob.error}</p>
            )}
            {doneJob && (
                <div role="alert" className="mt-3 space-y-3 rounded-[12px] border border-danger/30 bg-danger-bg px-3.5 py-3 text-sm text-danger-ink">
                    {doneJob.file_name && <p className='font-semibold text-ink'>{doneJob.file_name}</p>}
                    <div>
                        {groupFailures(doneJob.failed_pages).map((line, i) => (
                            <p key={i}>{line}</p>
                        ))}
                    </div>
                    <div className='flex flex-wrap gap-2'>
                        <Button size='sm' variant='secondary' onClick={retryPages}>Retry pages</Button>
                        <Button size='sm' onClick={continueWithFound}>{`Continue with ${(doneJob.questions || []).length} questions`}</Button>
                    </div>
                </div>
            )}

            {isExtracting && !doneJob && (
                <div className="mt-3 space-y-2 rounded-[14px] border border-line bg-mist p-3.5" role="status">
                    <div className='flex items-center gap-3 text-slate'>
                        <svg className='h-5 w-5 flex-shrink-0 animate-spin' viewBox='0 0 24 24' fill='none'>
                            <circle cx='12' cy='12' r='10' stroke='currentColor' strokeOpacity='0.2' strokeWidth='4' />
                            <path d='M22 12a10 10 0 00-10-10' stroke='currentColor' strokeWidth='4' strokeLinecap='round' />
                        </svg>
                        <div className='min-w-0 flex-1 text-sm font-medium text-ink'>
                            {reading ? <span>Reading the file…</span> : <span>{`Reading page ${prog!.pages_done} of ${prog!.pages_total}`}</span>}
                            {job?.file_name && <span className='block truncate text-xs font-normal text-muted'>{job.file_name}</span>}
                        </div>
                        <Button size='sm' variant='secondary' onClick={startOver}>Start over</Button>
                    </div>
                    {!reading && (
                        <div>
                            <div className='mb-1 flex items-center justify-between text-xs text-muted'>
                                <span>{`${found} questions found`}</span>
                                <span className='font-mono'>{Math.round(pct)}%</span>
                            </div>
                            <Track value={pct} label="Extraction progress" />
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default ExtractQuestionsFromFile
